import { dismissNudge, nudgeMarkCompleted } from './createNudgeInfoBarViewState.locstring.json';
import { getFeedbackRibbonForNudgeInfoBar } from './getFeedbackRibbonForNudgeInfoBar';
import { NudgeInfoBarResponse, onNudgeInfoBarClicked } from '../actions/onNudgeInfoBarClicked';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import addInfoBarMessage from 'owa-info-bar/lib/actions/addInfoBarMessage';
import removeInfoBarMessage from 'owa-info-bar/lib/actions/removeInfoBarMessage';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import Message from 'owa-service/lib/contract/Message';
import {
    getNudgedReason,
    getNudgedRowKeyFromItemId,
    getStore as nudgeStore,
    isSpotlightItem,
} from 'owa-mail-nudge-store';
import {
    InfoBarCustomAction,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

/**
 * Create the nudged info bar if necessary
 * @param message the message
 * @param key the key of info bar message
 * @param itemViewModel
 * @return the nudged InfoBarMessageViewState
 */
export default function createNudgeInfoBarViewState(
    message: Message,
    key: string,
    itemViewState: ItemViewState
): InfoBarMessageViewState {
    const rowKey = getNudgedRowKeyFromItemId(message.ItemId.Id);

    /**
     * If item is not a nudged item or is a spotlight item, then we should not
     * render the nudge info bar.
     */
    if (!rowKey || isSpotlightItem(message.ItemId.Id)) {
        return null;
    }

    const dismissAction: InfoBarCustomAction = {
        text: loc(dismissNudge),
        action: () => {
            const nudge = nudgeStore().nudgedRows.filter(
                nudgedRow => nudgedRow.rowKey === rowKey
            )[0];

            onNudgeInfoBarClicked(rowKey, message.ItemId.Id, NudgeInfoBarResponse.Dismiss);
            removeInfoBarMessage(itemViewState, key);

            if (isFeatureEnabled('tri-nudgeFeedback')) {
                const messageBody = message.UniqueBody
                    ? message.UniqueBody.Value
                    : message.NormalizedBody
                    ? message.NormalizedBody.Value
                    : '';

                const emailDonationJson = JSON.stringify({
                    messageBody: messageBody,
                    internetMessageId: message.InternetMessageId,
                    itemId: message.ItemId.Id,
                });

                // request for nudge feedback
                addInfoBarMessage(itemViewState, 'nudged', () => {
                    return <InfoBarMessageViewState>{
                        key: key,
                        source: InfoBarMessageSource.ReadingPane,
                        rank: InfoBarMessageRank.Info,
                        messageElement: getFeedbackRibbonForNudgeInfoBar(
                            itemViewState,
                            key,
                            nudge.reason,
                            emailDonationJson
                        ),
                    };
                });
            }
        },
    };

    const markCompletedAction: InfoBarCustomAction = {
        text: loc(nudgeMarkCompleted),
        action: () => {
            onNudgeInfoBarClicked(rowKey, message.ItemId.Id, NudgeInfoBarResponse.MarkComplete);
            removeInfoBarMessage(itemViewState, key);
        },
    };

    return <InfoBarMessageViewState>{
        key: key,
        source: InfoBarMessageSource.ReadingPane,
        rank: InfoBarMessageRank.Info,
        messageParts: [getNudgedReason(rowKey), dismissAction, '|', markCompletedAction],
        textColor: InfoBarMessageColor.Green20,
        isMessageSemibold: true,
    };
}
